import React, { useMemo, useCallback } from 'react';
import { I18n } from '@aws-amplify/core';
import { Form, Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { ACTION_PLAN_COLUMN_VALUES, RESPONSIVE_COLUMN_GROUPS } from '../constants';
import * as Typography from '@/components/Settings/DataExtraction/styles';
import * as S from '../styles';

const { useFormInstance, useWatch } = Form;

interface ActionPlansColumnsProps {
	disabled?: boolean;
}

export function ActionPlansColumns({ disabled = false }: Readonly<ActionPlansColumnsProps>) {
	const form = useFormInstance();
	const selectedColumns = useWatch(['action_plans', 'selected_columns'], form) || [];

	const allValues = useMemo(() => ACTION_PLAN_COLUMN_VALUES, []);

	const { checkAll, indeterminate } = useMemo(
		() => ({
			checkAll: allValues.length === selectedColumns.length,
			indeterminate: selectedColumns.length > 0 && selectedColumns.length < allValues.length
		}),
		[allValues.length, selectedColumns.length]
	);

	const onCheckAllChange = useCallback(
		(e: CheckboxChangeEvent) => {
			const list = e.target.checked ? [...allValues] : [];
			form.setFieldValue(['action_plans', 'selected_columns'], list);
		},
		[allValues, form]
	);

	return (
		<>
			<Typography.BodyLargeRegular>
				{I18n.get('Select the information you want to include in the extraction:')}
			</Typography.BodyLargeRegular>
			<S.CheckboxContainer>
				<S.CheckboxColumns>
					<S.Column>
						<Checkbox
							indeterminate={indeterminate}
							onChange={onCheckAllChange}
							checked={checkAll}
							disabled={disabled}
						>
							<Typography.LabelMediumRegular>{I18n.get('All')}</Typography.LabelMediumRegular>
						</Checkbox>
						<Form.Item name={['action_plans', 'selected_columns']}>
							<Checkbox.Group disabled={disabled}>
								{RESPONSIVE_COLUMN_GROUPS[0]?.options.map((option) => (
									<S.ResponsiveCheckbox
										key={option.value}
										value={option.value}
										disabled={disabled}
										className={option.className}
									>
										<Typography.LabelMediumRegular>
											{I18n.get(option.label)}
										</Typography.LabelMediumRegular>
									</S.ResponsiveCheckbox>
								))}
							</Checkbox.Group>
						</Form.Item>
					</S.Column>
					{RESPONSIVE_COLUMN_GROUPS.slice(1).map((group, index) => (
						<S.Column key={index + 1}>
							<Form.Item name={['action_plans', 'selected_columns']}>
								<Checkbox.Group disabled={disabled}>
									{group.options.map((option) => (
										<S.ResponsiveCheckbox
											key={option.value}
											value={option.value}
											disabled={disabled}
											className={option.className}
										>
											<Typography.LabelMediumRegular>
												{I18n.get(option.label)}
											</Typography.LabelMediumRegular>
										</S.ResponsiveCheckbox>
									))}
								</Checkbox.Group>
							</Form.Item>
						</S.Column>
					))}
				</S.CheckboxColumns>
			</S.CheckboxContainer>
		</>
	);
}
